import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Typography, Button, Paper, Divider, Stack, Chip } from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import StarIcon from '@mui/icons-material/Star';
import { textoDuracion } from '../../common/helpers';
import { dinero } from '../../../utils/dinero';

// El formulario guarda los idiomas por su id (ej. 'espanol', 'english', 'francais').
// Al mostrarlos, se traducen a su nombre correcto (con acentos y mayúsculas).
const IDIOMA_LABELS = { espanol: 'Español', english: 'English', francais: 'Français' };
const idiomaLabel = (i) => IDIOMA_LABELS[String(i).toLowerCase()] || i;

const StatItem = ({ icon: Icon, label }) => (
  <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'primary.main', minWidth: 0 }}>
    <Icon sx={{ fontSize: 20, flexShrink: 0 }} />
    <Typography variant="body2" sx={{ minWidth: 0, wordBreak: 'break-word' }}>{label}</Typography>
  </Stack>
);

// `noDisponible`: el servicio aún no es público (vista previa de su dueño o de la persona invitada a impartirlo): no se contrata
const ServiceHeaderInfo = ({ service, isOwner, isFavorite, onToggleFavorite, onQuote, noDisponible = false }) => {
  const navigate = useNavigate();
  const { userId, serviceId } = useParams();

  // La reserva es una pantalla privada: sin sesión, PrivateRoute manda al login y al entrar regresa a la reserva
  const handleReservar = () => {
    navigate(`/servicio/${userId}/${serviceId}/reservar`);
  };
  // Al hacer click en una etiqueta, ir a la búsqueda filtrada por ese tag.
  // El backend hace match normalizado (sin acentos/mayúsculas), así que no importa cómo se escribió.
  const handleTagClick = (t) => navigate(`/buscar?tag=${encodeURIComponent(t)}`);
  const {
    nombre, costoServicio, tipoCosto, duracionEstimada, unidadDuracion, modalidades, idiomas, tags,
    ratingPromedio, ratingTotal
  } = service;
  const duracion = textoDuracion(duracionEstimada, unidadDuracion);

  return (
    <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: '12px', mb: 4 }} elevation={0} variant="outlined">
      {/* Top Row: Title, Rating, Recommendations, Price, Action.
          En móvil se apila (columna); en escritorio queda en una sola fila. */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', md: 'center' },
        gap: { xs: 1.5, md: 0 },
        mb: 2
      }}>
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5" fontWeight="bold" color="primary.main" sx={{ wordBreak: 'break-word' }}>
            {nombre}
          </Typography>
          {ratingPromedio > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              <StarIcon sx={{ color: 'primary.main', fontSize: 24 }} />
              <Typography variant="h6" fontWeight="bold" color="primary.main">
                {Number(ratingPromedio).toFixed(1)}
              </Typography>
            </Box>
          )}
        </Box>

        <Divider orientation="vertical" flexItem sx={{ mx: 3, display: { xs: 'none', md: 'block' } }} />

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {ratingTotal > 0 ? `${ratingTotal} ${ratingTotal === 1 ? 'opinión' : 'opiniones'}` : 'Sin opiniones aún'}
          </Typography>
        </Box>

        <Divider orientation="vertical" flexItem sx={{ mx: 3, display: { xs: 'none', md: 'block' } }} />

        <Box sx={{
          flex: { md: 1.5 },
          width: { xs: '100%', md: 'auto' },
          minWidth: 0,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: { xs: 'space-between', md: 'flex-end' },
          gap: { xs: 1.5, md: 3 }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
            <Typography variant="h4" fontWeight="bold" color="primary.main" sx={{ fontSize: { xs: '1.6rem', md: '2.125rem' } }}>
              {dinero(costoServicio)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '14px' }}>
              {tipoCosto || 'por servicio'}
            </Typography>
          </Box>
          <Box onClick={onToggleFavorite} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            {isFavorite ? (
              <FavoriteIcon color="primary" sx={{ fontSize: 28 }} />
            ) : (
              <FavoriteBorderIcon color="primary" sx={{ fontSize: 28 }} />
            )}
          </Box>
          {/* En móvil los botones toman su propia fila y se reparten el ancho */}
          <Box sx={{ display: 'flex', gap: 1.5, width: { xs: '100%', md: 'auto' }, minWidth: 0 }}>
            <Button
              variant="outlined"
              disabled={isOwner || noDisponible}
              onClick={onQuote}
              sx={{
                flex: { xs: 1, md: 'none' },
                minWidth: 0,
                textTransform: 'none',
                borderColor: 'divider',
                color: 'text.secondary',
                borderRadius: '8px',
                px: { xs: 2, md: 3 }
              }}
            >
              Cotizar
            </Button>
            <Button
              variant="contained"
              disabled={isOwner || noDisponible}
              onClick={handleReservar}
              sx={{
                flex: { xs: 1, md: 'none' },
                minWidth: 0,
                bgcolor: 'primary.main',
                textTransform: 'none',
                borderRadius: '8px',
                px: { xs: 2, md: 4 },
                py: 1.2,
                fontWeight: 'bold',
                fontSize: { xs: '14px', md: '16px' },
                whiteSpace: 'nowrap',
                '&.Mui-disabled': {
                  bgcolor: 'divider',
                  color: 'text.secondary'
                }
              }}
            >
              {isOwner ? 'Tu servicio' : noDisponible ? 'Aún no disponible' : 'Reservar'}
            </Button>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Bottom Row: Stats Icons */}
      <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'space-between' }, flexWrap: 'wrap', gap: { xs: 1.5, md: 3 } }}>
        {duracion && <StatItem icon={AccessTimeIcon} label={`Duración: ${duracion}`} />}
        <StatItem
          icon={LocationOnIcon}
          label={modalidades?.join(', ') || 'Remoto'}
        />
        <StatItem
          icon={ChatBubbleOutlineIcon}
          label={idiomas?.length ? idiomas.map(idiomaLabel).join(', ') : 'Español'}
        />
      </Box>

      {/* Etiquetas: cada una es clickeable y lleva a la búsqueda por ese tag */}
      {tags?.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <LocalOfferIcon sx={{ fontSize: 20, color: 'primary.main', flexShrink: 0, mt: 0.5 }} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {tags.map((t, i) => (
                <Chip
                  key={`${t}-${i}`}
                  label={t}
                  size="small"
                  clickable
                  onClick={() => handleTagClick(t)}
                  sx={{
                    bgcolor: 'sutil',
                    color: 'primary.main',
                    fontWeight: 600,
                    '&:hover': { bgcolor: 'secondary.main' }
                  }}
                />
              ))}
            </Box>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default ServiceHeaderInfo;
